import {
  AccessTime,
  ExpandMore,
  LocationOn,
  Description,
  NoteAlt,
} from '@mui/icons-material'
import { Box, Typography, Collapse, Tabs, Tab } from '@mui/material'
import { CSSProperties, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SecurityReportInfo } from 'types/assign'

// NOTE:未来の予定でもAssignCardを使う可能性があり、現時点では構造が分からないため、
// HisotyItemから必要なプロパティを切り出して使用している。
interface AssignCardProps {
  // 必須データ
  assignId: string
  date: string
  startTime: string
  endTime: string
  contractDetailName: string
  address: string

  // オプショナル（未来の予定では不要の可能性）
  securityNote?: string
  securityReport?: SecurityReportInfo | null

  // NOTE：未来の予定でもメモや報告書タブを使用するなら削除で良い
  // 機能フラグ
  showNoteTab?: boolean
  showReportTab?: boolean
  defaultExpanded?: boolean
}

export default function AssignCard({
  assignId,
  date,
  startTime,
  endTime,
  contractDetailName,
  address,
  securityNote = '',
  securityReport = null,
  showNoteTab = true,
  showReportTab = true,
  defaultExpanded = false,
}: AssignCardProps): JSX.Element {
  const { t } = useTranslation('page-assign-components')
  const [expanded, setExpanded] = useState(defaultExpanded)
  const [activeTab, setActiveTab] = useState(0)

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  const handleTabChange = (_: unknown, newValue: number) => {
    setActiveTab(newValue)
  }

  // 報告書タブを表示するか判定
  const shouldShowReportTab =
    showReportTab && securityReport?.exists && securityReport?.canView

  // メモと報告書の両方が非表示の場合、展開部分自体を非表示
  const hasExpandableContent = showNoteTab || shouldShowReportTab

  return (
    <Box key={assignId} sx={historyItemCardStyle}>
      <Box onClick={handleExpandClick} sx={cardHeaderStyle}>
        <Box sx={dateTimeContainerStyle}>
          <AccessTime sx={iconStyle} />
          <Typography sx={dateTimeTextStyle}>
            {date} {startTime}~{endTime}
          </Typography>
        </Box>

        <Box sx={contractDetailNameRowStyle}>
          <Box sx={contractDetailNameContainerStyle}>
            <LocationOn sx={contractDetailIconStyle} />
            <Typography sx={contractDetailNameTextStyle}>
              {contractDetailName}
            </Typography>
          </Box>
          {hasExpandableContent && (
            <ExpandMore
              sx={{
                ...expandIconStyle,
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          )}
        </Box>

        <Box sx={addressContainerStyle}>
          <LocationOn sx={iconStyle} />
          <Typography sx={addressTextStyle}>{address}</Typography>
        </Box>
      </Box>

      {hasExpandableContent && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={collapseContentStyle}>
            <Tabs value={activeTab} onChange={handleTabChange} sx={tabsStyle}>
              {showNoteTab && (
                <Tab
                  icon={<NoteAlt />}
                  iconPosition="start"
                  label={t('assign_card.tab_memo')}
                  sx={tabStyle}
                />
              )}
              {shouldShowReportTab && (
                <Tab
                  icon={<Description />}
                  iconPosition="start"
                  label={t('assign_card.tab_report')}
                  sx={tabStyle}
                />
              )}
            </Tabs>

            <Box sx={tabContentStyle}>
              {activeTab === 0 && showNoteTab ? (
                <Typography sx={noteTextStyle}>
                  {securityNote || t('assign_card.no_memo')}
                </Typography>
              ) : (
                <Box
                  onClick={() => {
                    if (
                      securityReport?.contractDetailId &&
                      securityReport?.date
                    ) {
                      location.href = `/security-report?contract_detail_id=${securityReport.contractDetailId}&date=${securityReport.date}&slide=right`
                    }
                  }}
                  sx={reportLinkStyle}
                >
                  <Typography sx={reportLinkTextStyle}>
                    {t('assign_card.view_report')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Collapse>
      )}
    </Box>
  )
}

// Styles
const historyItemCardStyle: CSSProperties = {
  backgroundColor: '#ffffff',
  border: '2px solid #f2f2f2',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
}

const cardHeaderStyle = {
  padding: '20px 16px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
}

const dateTimeContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '12px',
}

const iconStyle: CSSProperties = {
  fontSize: 28,
  color: '#666',
}

const dateTimeTextStyle: CSSProperties = {
  fontSize: 20,
  fontWeight: 'normal',
  color: '#333',
}

const contractDetailNameRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '12px',
}

const contractDetailNameContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}

const contractDetailIconStyle: CSSProperties = {
  fontSize: 28,
  color: '#14b7ae',
}

const contractDetailNameTextStyle: CSSProperties = {
  fontSize: 18,
  fontWeight: 'bold',
  color: '#333',
}

const addressContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}

const addressTextStyle: CSSProperties = {
  fontSize: 16,
  color: '#666',
}

const expandIconStyle: CSSProperties = {
  fontSize: 32,
  color: '#666',
  transition: 'transform 0.3s',
}

const collapseContentStyle: CSSProperties = {
  borderTop: '1px solid #e0e0e0',
}

const tabsStyle = {
  borderBottom: '1px solid #e0e0e0',
  '& .MuiTab-root': {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#666',
    minHeight: 52,
  },
  '& .Mui-selected': {
    color: '#14b7ae !important',
  },
  '& .MuiTabs-indicator': {
    backgroundColor: '#14b7ae',
    height: 3,
  },
}

const tabStyle = {
  flex: 1,
}

const tabContentStyle: CSSProperties = {
  padding: '18px 16px',
}

const noteTextStyle: CSSProperties = {
  fontSize: 16,
  color: '#333',
  whiteSpace: 'pre-wrap',
  lineHeight: 1.8,
}

const reportLinkStyle = {
  cursor: 'pointer',
  textAlign: 'center',
}

const reportLinkTextStyle: CSSProperties = {
  fontSize: 16,
  fontWeight: 'bold',
  color: '#14b7ae',
}
